"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "@/lib/auth/session";
import { REQUIRES_AUTH_ATTR, isGatedPath, signupHref } from "@/lib/auth/guest-gate";

/**
 * For signed-out visitors, turns clicks on challenge / lesson CTAs (Solve, Continue,
 * Start learning, …) into a trip to /signup?next=<target>, so they land on the
 * challenge right after creating an account.
 * Uses one capture-phase listener instead of touching every CTA; the pages themselves
 * stay public for direct visits and search engines.
 */
export function GuestCtaGate() {
  const { status } = useSession();
  const router = useRouter();

  useEffect(() => {
    if (status !== "anonymous") return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.hasAttribute("download")) return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (!isGatedPath(url.pathname) && !anchor.hasAttribute(REQUIRES_AUTH_ATTR)) return;
      // Already on the target (e.g. a self-link): nothing to gate.
      if (url.pathname === window.location.pathname) return;
      event.preventDefault();
      event.stopPropagation();
      router.push(signupHref(`${url.pathname}${url.search}`));
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [status, router]);

  return null;
}
